import { TIERS, type Tier } from '../constants/index.js';

/**
 * Tier feature entitlements — the single source of truth for
 * "does this tenant's plan include feature X?".
 *
 * Consumed by BOTH the web app and the native apps (e.g. Model B branded-app
 * gating). Seeded from business/strategy/PRICING.md's key feature gates. This module ships the
 * pure decision function only; enforcement is wired per-surface separately
 * (never gate a live paid surface into invisibility — see the no-ghost-features
 * rule in CLAUDE.md).
 *
 * Model: each tier has a numeric rank on the main commodity ladder; a feature
 * declares the minimum rank that unlocks it, so `hasFeature` is `rank >= min`.
 * Nearly every business/strategy/PRICING.md gate is "tier X and above", which this expresses
 * directly; an "Enterprise only" gate is just `minRank = Enterprise` (and
 * Enterprise Plus, being higher, inherits it — correct).
 */

/**
 * Rank on the main pricing ladder (higher = more capable), price-ordered per
 * business/strategy/PRICING.md: Solo < Starter < Growth < Pro < Elite < Enterprise < Enterprise Plus.
 *
 * ⚠️ The three VERTICAL tiers (fundraiser, tcg_starter, storage) are slotted by
 * their nearest commodity equivalent as a PROVISIONAL default — their exact
 * feature entitlements are NOT fully specified in business/strategy/PRICING.md's 6-column tables.
 * TODO(jim): confirm vertical-tier entitlements before relying on vertical-tenant gating.
 */
export const TIER_RANK: Record<Tier, number> = {
  /**
   * Comped accounts clear every gate — including gates added after this line was
   * written, which is why it sits ABOVE Enterprise Plus rather than level with
   * it. A comp that silently stops covering a new feature is the failure mode
   * this rank exists to prevent.
   */
  [TIERS.COMPLIMENTARY]: 99,
  /**
   * A trial shows the WHOLE product. Jim, 2026-08-25: "For the demo, why not
   * show all available features? We would make a note that it is Professional
   * tier option."
   *
   * Above Enterprise Plus for the same reason `complimentary` is — a gate added
   * next month must be covered without anyone remembering to revisit this line.
   * Below `complimentary` because the two are not the same promise: a comp is
   * permanent, a trial expires. Nothing keys on the gap today; it exists so
   * that when expiry lands it has somewhere to sit.
   *
   * ⚠️ Unlocking is only half of what Jim asked for. The other half is the
   * label — see `featureTierLabel()` below. A trial that shows Pro features
   * with no indication they are Pro features misrepresents the plan the
   * evaluator is about to buy.
   */
  [TIERS.TRIAL]: 98,
  [TIERS.SOLO]: 1,
  [TIERS.FUNDRAISER]: 2, // vertical ($79, nonprofit) — Starter-class default
  [TIERS.STARTER]: 2,
  [TIERS.TCG_STARTER]: 2, // vertical (trading-card starter) — Starter-class default
  [TIERS.STORAGE]: 6, // vertical (storage-lien; business/strategy/PRICING.md "Storage Enterprise $1,500+") — Enterprise-class default
  [TIERS.GROWTH]: 3,
  [TIERS.PRO]: 4,
  [TIERS.ELITE]: 5,
  [TIERS.ENTERPRISE]: 6,
  [TIERS.ENTERPRISE_PLUS]: 7,
};

/** Gated feature keys. Extend as the matrix fills out from business/strategy/PRICING.md. */
export const FEATURES = {
  CONSIGNOR_STATEMENTS: 'consignor_statements',
  CUSTOM_DOMAIN: 'custom_domain',
  MARKETPLACE_LISTING: 'marketplace_listing',
  WHITE_LABEL_REMOVAL: 'white_label_removal',
  SHOPIFY_INTEGRATION: 'shopify_integration',
  AI_CATALOGING: 'ai_cataloging',
  /** Model B — tenant's own-branded native app in the stores. Pro+ (decided 2026-07-23). */
  BRANDED_APPS: 'branded_apps',
  LIVE_SIMULCAST: 'live_simulcast',
  MULTI_LOCATION: 'multi_location',
  API_ACCESS: 'api_access',
  DATA_WAREHOUSE_EXPORT: 'data_warehouse_export',
  DEDICATED_CSM: 'dedicated_csm',
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

/** Minimum ladder rank that unlocks each feature. Source: business/strategy/PRICING.md key feature gates. */
const FEATURE_MIN_RANK: Record<FeatureKey, number> = {
  [FEATURES.CONSIGNOR_STATEMENTS]: TIER_RANK[TIERS.STARTER], // Starter+
  [FEATURES.CUSTOM_DOMAIN]: TIER_RANK[TIERS.GROWTH], // Growth+
  [FEATURES.MARKETPLACE_LISTING]: TIER_RANK[TIERS.GROWTH], // Growth+
  [FEATURES.WHITE_LABEL_REMOVAL]: TIER_RANK[TIERS.PRO], // Pro+
  [FEATURES.SHOPIFY_INTEGRATION]: TIER_RANK[TIERS.PRO], // Pro+
  [FEATURES.AI_CATALOGING]: TIER_RANK[TIERS.PRO], // Pro+
  [FEATURES.BRANDED_APPS]: TIER_RANK[TIERS.PRO], // Pro+ (Model B)
  [FEATURES.LIVE_SIMULCAST]: TIER_RANK[TIERS.ELITE], // Elite+
  [FEATURES.MULTI_LOCATION]: TIER_RANK[TIERS.ELITE], // Elite+
  [FEATURES.API_ACCESS]: TIER_RANK[TIERS.ELITE], // Elite+
  [FEATURES.DATA_WAREHOUSE_EXPORT]: TIER_RANK[TIERS.ENTERPRISE], // Enterprise+
  [FEATURES.DEDICATED_CSM]: TIER_RANK[TIERS.ENTERPRISE], // Enterprise+
};

/**
 * Does a tenant on `tier` have access to `feature`?
 * Fail-safe: an unknown tier or feature returns `false` (deny by default).
 */
export function hasFeature(tier: Tier, feature: FeatureKey): boolean {
  const rank = TIER_RANK[tier];
  const min = FEATURE_MIN_RANK[feature];
  if (rank === undefined || min === undefined) return false;
  return rank >= min;
}

/**
 * The tier that actually carries each feature — the naming half of Jim's
 * "make a note that it is Professional tier option".
 *
 * Derived from FEATURE_MIN_RANK rather than hand-listed, so a feature can never
 * be unlocked at one rank and advertised at another. The two would drift the
 * first time a gate moved, and the label is the thing a prospect makes a buying
 * decision on.
 *
 * Ranks are resolved against the COMMODITY ladder only. `complimentary` (99)
 * and `trial` (98) sit above it deliberately and are not answers to "which plan
 * do I need to buy?" — they are how you got in without buying one.
 */
const COMMODITY_LADDER: readonly Tier[] = [
  TIERS.SOLO,
  TIERS.STARTER,
  TIERS.GROWTH,
  TIERS.PRO,
  TIERS.ELITE,
  TIERS.ENTERPRISE,
  TIERS.ENTERPRISE_PLUS,
];

/** Customer-facing plan names. What a prospect sees, not the internal key. */
export const TIER_LABEL: Record<Tier, string> = {
  [TIERS.COMPLIMENTARY]: 'Complimentary',
  [TIERS.TRIAL]: 'Trial',
  [TIERS.SOLO]: 'Solo',
  [TIERS.FUNDRAISER]: 'Fundraiser',
  [TIERS.STARTER]: 'Starter',
  [TIERS.TCG_STARTER]: 'Trading Card Starter',
  [TIERS.STORAGE]: 'Storage',
  [TIERS.GROWTH]: 'Growth',
  [TIERS.PRO]: 'Professional',
  [TIERS.ELITE]: 'Elite',
  [TIERS.ENTERPRISE]: 'Enterprise',
  [TIERS.ENTERPRISE_PLUS]: 'Enterprise Plus',
};

/**
 * The cheapest commodity plan that includes `feature`, as a customer-facing
 * name — e.g. `'Professional'`. Returns `null` for an unknown feature.
 *
 * Use this to caption a feature a tenant can currently see but is not paying
 * for: "Included with Professional". Callers on BOTH the website and the native
 * apps must read THIS function rather than re-deriving the wording, or the two
 * surfaces will eventually name different plans for the same feature.
 */
export function featureTierLabel(feature: FeatureKey): string | null {
  const min = FEATURE_MIN_RANK[feature];
  if (min === undefined) return null;
  const tier = COMMODITY_LADDER.find((t) => TIER_RANK[t] >= min);
  return tier ? TIER_LABEL[tier] : null;
}

/**
 * Is `tier` seeing `feature` on borrowed time — entitled today, but not by any
 * plan they are actually paying for? True only for trials.
 *
 * This is the condition that should render the label. A comp is NOT included:
 * comped access is the arrangement, not a preview of one, and captioning every
 * feature "Included with Professional" for a comped customer would read as a
 * sales pitch aimed at someone who already has the product for free.
 */
export function isPreviewingFeature(tier: Tier, feature: FeatureKey): boolean {
  if (tier !== TIERS.TRIAL) return false;
  return hasFeature(tier, feature);
}
